/**
 * NaceTree (#140): searchable two-level NACE tree (sections, divisions) with
 * checkboxes for the Compare panel. Sections are collapsed by default; a
 * filter shows matching divisions with their section expanded. Checking a
 * section selects only the section code. Knows no services; reports the
 * selected codes through onChange.
 */

import template from './nace-tree.template.html?raw';
import type { TranslationParams } from '../i18n/ui-manager.types';
import type { Facets } from '../core/compare-service';

export interface NaceTreeOptions {
  t: (key: string, params?: TranslationParams) => string;
  sections: Facets['nace'];
  selected: string[];
  onChange(codes: string[]): void;
}

export interface NaceTree {
  readonly element: HTMLElement;
  setSelected(codes: string[]): void;
  filter(query: string): void;
}

interface SectionView {
  root: HTMLElement;
  toggle: HTMLButtonElement;
  divisions: HTMLElement;
  rows: { code: string; row: HTMLElement; text: string }[];
  text: string;
  label: string;
  expanded: boolean;
}

let idCounter = 0;

export function createNaceTree(opts: NaceTreeOptions): NaceTree {
  const { t, sections, onChange } = opts;
  let selected = [...opts.selected];

  const element = document.createElement('div');
  element.className = 'nace-tree';
  element.innerHTML = template;

  const search = element.querySelector<HTMLInputElement>('[data-nace-search]')!;
  search.placeholder = t('compare.nace.searchPlaceholder');
  search.setAttribute('aria-label', t('compare.nace.searchLabel'));
  const box = element.querySelector<HTMLElement>('[data-nace-box]')!;
  const empty = element.querySelector<HTMLElement>('[data-nace-empty]')!;
  empty.textContent = t('compare.nace.noResults');

  const checkboxes = new Map<
    string,
    { input: HTMLInputElement; row: HTMLLabelElement }
  >();

  const makeRow = (code: string, label: string, cls: string) => {
    const wrap = document.createElement('label');
    wrap.className = cls;
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.dataset.naceCheck = code;
    input.checked = selected.includes(code);
    input.addEventListener('change', () => {
      selected = input.checked
        ? [...selected.filter((c) => c !== code), code]
        : selected.filter((c) => c !== code);
      syncChecks();
      onChange([...selected]);
    });
    checkboxes.set(code, { input, row: wrap });
    const text = document.createElement('span');
    text.textContent = `${code} ${label}`;
    wrap.append(input, text);
    return wrap;
  };

  const syncChecks = () => {
    for (const [code, { input, row }] of checkboxes) {
      input.checked = selected.includes(code);
      row.classList.toggle('is-selected', input.checked);
    }
  };

  const views: SectionView[] = sections.map((section) => {
    const root = document.createElement('div');
    root.className = 'nace-tree__section';
    root.dataset.naceSection = section.code;

    const head = document.createElement('div');
    head.className = 'nace-tree__section-head';
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'nace-tree__toggle';
    toggle.dataset.naceToggle = '';
    head.append(toggle, makeRow(section.code, section.label, 'nace-tree__row'));

    const divisions = document.createElement('div');
    divisions.className = 'nace-tree__divisions';
    divisions.id = `nace-divisions-${++idCounter}`;
    toggle.setAttribute('aria-controls', divisions.id);
    const rows = section.divisions.map((d) => {
      const row = makeRow(d.code, d.label, 'nace-tree__row');
      const holder = document.createElement('div');
      holder.dataset.naceDivision = d.code;
      holder.append(row);
      divisions.append(holder);
      return {
        code: d.code,
        row: holder,
        text: `${d.code} ${d.label}`.toLowerCase(),
      };
    });

    root.append(head, divisions);
    box.append(root);
    const view: SectionView = {
      root,
      toggle,
      divisions,
      rows,
      text: `${section.code} ${section.label}`.toLowerCase(),
      label: section.label,
      expanded: false,
    };
    toggle.addEventListener('click', () => {
      view.expanded = !view.expanded;
      render(search.value);
    });
    return view;
  });

  function render(query: string) {
    const q = query.trim().toLowerCase();
    let anyShown = false;
    for (const v of views) {
      let show: boolean;
      let open: boolean;
      if (!q) {
        show = true;
        open = v.expanded;
        v.rows.forEach((r) => (r.row.hidden = false));
      } else {
        const sectionMatch = v.text.includes(q);
        let matches = 0;
        for (const r of v.rows) {
          r.row.hidden = !(sectionMatch || r.text.includes(q));
          if (!r.row.hidden) matches++;
        }
        show = sectionMatch || matches > 0;
        open = matches > 0;
      }
      v.root.hidden = !show;
      v.divisions.hidden = !open;
      v.toggle.textContent = open ? '▾' : '▸';
      v.toggle.setAttribute('aria-expanded', String(open));
      v.toggle.setAttribute(
        'aria-label',
        t(open ? 'compare.nace.collapse' : 'compare.nace.expand', {
          label: v.label,
        })
      );
      v.toggle.disabled = Boolean(q);
      if (show) anyShown = true;
    }
    empty.hidden = anyShown;
  }

  search.addEventListener('input', () => render(search.value));
  syncChecks();
  render('');

  return {
    element,
    setSelected(codes) {
      selected = [...codes];
      syncChecks();
    },
    filter(query) {
      search.value = query;
      render(query);
    },
  };
}
